import { useRef, useState, type FormEvent } from 'react';
import { cognito, completeSignIn } from '../lib/auth';
import { AuthError, type AuthChallenge, type SignInResult } from '../lib/cognito';
import './Login.css';

function Icon({ name, size = 20 }: { name: 'arrow' | 'lock' | 'mail' | 'eye' | 'eyeOff' | 'back' | 'check'; size?: number }) {
  const paths = {
    arrow: <><path d="M4 12h15M13 6l6 6-6 6" /></>,
    back: <><path d="M20 12H5m6-6-6 6 6 6" /></>,
    lock: <><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3m-4 5v2" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 7 8 6 8-6" /></>,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></>,
    eyeOff: <><path d="m3 3 18 18M10 5.2 12 5c6.5 0 10 7 10 7a19 19 0 0 1-3 3.8M6.3 6.3A19 19 0 0 0 2 12s3.5 7 10 7c1.7 0 3.2-.5 4.5-1.2M10 10a3 3 0 0 0 4 4" /></>,
    check: <path d="m5 12 4 4L19 6" />,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function PixelMark() {
  return <span className="login-pixel-mark" aria-hidden="true"><i /><i /><i /><i /><i /></span>;
}

function PasswordField({ id, label, value, onChange, isNew = false }: {
  id: string; label: string; value: string; onChange: (value: string) => void; isNew?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  return <div className="login-field">
    <label htmlFor={id}>{label}</label>
    <div className="login-input-wrap">
      <Icon name="lock" />
      <input id={id} name={id} type={visible ? 'text' : 'password'} required
        autoComplete={isNew ? 'new-password' : 'current-password'} minLength={isNew ? 8 : undefined}
        value={value} onChange={e => onChange(e.target.value)} placeholder={isNew ? 'At least 8 characters' : 'Enter your password'} />
      <button type="button" className="login-reveal" onClick={() => setVisible(v => !v)}
        aria-label={`${visible ? 'Hide' : 'Show'} ${label.toLowerCase()}`} aria-pressed={visible}>
        <Icon name={visible ? 'eyeOff' : 'eye'} size={19} />
      </button>
    </div>
  </div>;
}

const attributeLabels: Record<string, string> = {
  name: 'Full name', given_name: 'First name', family_name: 'Last name', email: 'Email', phone_number: 'Phone number with country code',
};

export default function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [mode, setMode] = useState<'login' | 'forgot' | 'reset' | 'challenge'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [destination, setDestination] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);
  const isMfa = mode === 'challenge' && challenge?.type === 'mfa';
  const isNewPassword = mode === 'reset' || (mode === 'challenge' && challenge?.type === 'newPassword');

  function changeMode(next: typeof mode) {
    setMode(next);
    setError('');
    setNotice('');
    setPassword('');
    setConfirmation('');
    setCode('');
    setAttributes({});
    if (next !== 'challenge') setChallenge(null);
    requestAnimationFrame(() => heading.current?.focus());
  }

  function acceptResult(result: SignInResult) {
    setPassword('');
    setConfirmation('');
    setCode('');
    if (result.type === 'signedIn') {
      completeSignIn(result);
      onAuthenticated();
    } else {
      changeMode('challenge');
      setChallenge(result);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current) return;
    setError('');
    setNotice('');
    if (isNewPassword && password !== confirmation) {
      setError('Passwords must match. Please check your confirmation.');
      return;
    }
    submitting.current = true;
    setBusy(true);
    try {
      if (mode === 'login') {
        acceptResult(await cognito.signIn(email, password));
      } else if (mode === 'forgot') {
        const target = await cognito.forgotPassword(email);
        setDestination(target);
        changeMode('reset');
      } else if (mode === 'reset') {
        await cognito.resetPassword(email, code, password);
        changeMode('login');
        setNotice('Password updated. You can now sign in to your account.');
      } else if (challenge) {
        acceptResult(await cognito.completeChallenge(challenge, isMfa ? code.trim() : password, attributes));
      }
    } catch (e) {
      if (e instanceof AuthError && e.code === 'PasswordResetRequiredException') {
        changeMode('forgot');
        setNotice('Reset your password to regain access to your account.');
      } else {
        setError(e instanceof Error ? e.message : 'Unable to sign in. Please try again.');
      }
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  const content = mode === 'login'
    ? { eyebrow: 'YOUR WORKSPACE', title: 'Welcome back.', description: 'Sign in to your account and manage your campaigns.', action: 'Sign in to my account' }
    : mode === 'forgot'
      ? { eyebrow: 'ACCOUNT RECOVERY', title: 'Forgot your password?', description: 'Enter your account email to receive a recovery code.', action: 'Send code' }
      : mode === 'reset'
        ? { eyebrow: 'ACCOUNT RECOVERY', title: 'A fresh start.', description: destination ? `We sent a code to ${destination}. Use it to set your new password.` : 'If an account exists for this email, you will receive a code to reset your password.', action: 'Save new password' }
        : isMfa
          ? { eyebrow: 'SECURITY VERIFICATION', title: 'Just one more step.', description: challenge?.name === 'SOFTWARE_TOKEN_MFA' ? 'Enter the code from your authenticator app.' : `Enter the code sent${challenge?.destination ? ` to ${challenge.destination}` : ' to you'}.`, action: 'Verify and sign in' }
          : { eyebrow: 'FIRST-TIME SIGN IN', title: 'Your account, your password.', description: 'Choose a personal password to finish setting up your account.', action: 'Set password and sign in' };

  return <main className="login-page">
    <section className="login-story" aria-label="PixelDMP — audience intelligence">
      <a className="login-brand" href="/" aria-label="PixelDMP, home"><PixelMark /><span>pixel<span className="login-brand-suffix">DMP</span></span></a>
      <div className="login-story-copy">
        <span className="login-story-kicker"><span /> AUDIENCE INTELLIGENCE</span>
        <h1>Turn data<br />into <em>connections.</em></h1>
        <p>Know your audience. Activate your campaigns.<br className="login-desktop-break" /> Discover new possibilities in every pixel.</p>
      </div>
      <div className="login-network" aria-hidden="true">
        <div className="login-orbit login-orbit-outer" /><div className="login-orbit login-orbit-inner" />
        <svg className="login-network-lines" viewBox="0 0 540 290" fill="none">
          <path d="M105 80H190L260 148H363L430 207M133 223H192L260 148V43M260 148L380 61M65 163H172L260 148" />
          <circle cx="105" cy="80" r="5" /><circle cx="430" cy="207" r="5" /><circle cx="133" cy="223" r="5" /><circle cx="380" cy="61" r="5" /><circle cx="260" cy="43" r="4" /><circle cx="65" cy="163" r="4" />
        </svg>
        <div className="login-network-core"><PixelMark /></div>
        <span className="login-network-label login-network-label-one"><span className="login-network-dot" />Audiences</span>
        <span className="login-network-label login-network-label-two"><Icon name="check" size={15} />Connected campaigns</span>
        <span className="login-network-pixel login-network-pixel-one" /><span className="login-network-pixel login-network-pixel-two" /><span className="login-network-pixel login-network-pixel-three" />
      </div>
      <div className="login-story-footer"><span className="login-footer-line" />Connected data. Meaningful results.</div>
    </section>

    <section className="login-access" aria-label="Account access">
      <div className="login-mobile-brand"><PixelMark /><span>pixel<strong>DMP</strong></span></div>
      <div className="login-form-shell">
        <div className="login-welcome-icon"><Icon name={mode === 'login' ? 'lock' : 'mail'} size={23} /></div>
        <p className="login-eyebrow">{content.eyebrow}</p>
        <h2 ref={heading} tabIndex={-1}>{content.title}</h2>
        <p className="login-description">{content.description}</p>

        {error && <div id="login-error" className="login-message login-message-error" role="alert">{error}</div>}
        {notice && <div className="login-message login-message-success" role="status"><Icon name="check" size={18} /><span>{notice}</span></div>}

        <form onSubmit={submit} aria-label={content.title} aria-describedby={error ? 'login-error' : undefined} aria-busy={busy}>
          <fieldset disabled={busy}>
            {(mode === 'login' || mode === 'forgot') && <div className="login-field">
              <label htmlFor="login-email">Email</label>
              <div className="login-input-wrap"><Icon name="mail" />
                <input id="login-email" name="email" type="email" autoComplete="username" autoCapitalize="none" spellCheck={false}
                  required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />
              </div>
            </div>}
            {(mode === 'reset' || isMfa) && <div className="login-field">
              <label htmlFor="login-code">Verification code</label>
              <div className="login-input-wrap"><Icon name="lock" />
                <input id="login-code" name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
                  required value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" />
              </div>
            </div>}
            {mode === 'challenge' && challenge?.requiredAttributes.map(attribute => <div className="login-field" key={attribute}>
              <label htmlFor={`login-attribute-${attribute}`}>{attributeLabels[attribute] || attribute}</label>
              <div className="login-input-wrap"><input id={`login-attribute-${attribute}`} required
                type={attribute === 'email' ? 'email' : attribute === 'phone_number' ? 'tel' : 'text'}
                value={attributes[attribute] || ''} onChange={e => setAttributes(a => ({ ...a, [attribute]: e.target.value }))} /></div>
            </div>)}
            {(mode === 'login' || isNewPassword) && <PasswordField id="login-password" label={isNewPassword ? 'New password' : 'Password'}
              value={password} onChange={setPassword} isNew={isNewPassword} />}
            {isNewPassword && <PasswordField id="login-confirmation" label="Confirm new password" value={confirmation} onChange={setConfirmation} isNew />}
            {mode === 'login' && <div className="login-forgot-row"><button type="button" className="login-text-button" onClick={() => changeMode('forgot')}>Forgot password?</button></div>}
            <button type="submit" className="login-submit">{busy ? <><span className="login-spinner" />Please wait…</> : <>{content.action}<Icon name="arrow" size={19} /></>}</button>
            {mode !== 'login' && <button type="button" className="login-back" onClick={() => changeMode('login')}><Icon name="back" size={17} />Back to sign in</button>}
            {mode === 'reset' && <button type="button" className="login-resend" onClick={() => changeMode('forgot')}>Did not receive a code? Request another</button>}
          </fieldset>
        </form>
        <div className="login-account-help"><span />
          <p>First time here? Use the email and temporary password<br className="login-desktop-break" /> provided by your account administrator.</p>
        </div>
      </div>
      <footer className="login-access-footer"><span>© {new Date().getFullYear()} PixelDMP</span><div><a href="/_api/en/privacy" target="_blank" rel="noreferrer">Privacy</a><span>·</span><a href="/_api/en/terms" target="_blank" rel="noreferrer">Terms of use</a></div></footer>
    </section>
  </main>;
}
